'use client'

import { useCallback, useMemo } from 'react'

import { methodInfoStyles } from '@/core/components/MethodInfoBlock'
import { TypeGraftabilityHint } from '@/core/components/TypeGraftabilityHint'
import { useLibraries } from '@/core/hooks'
import { useCodeStore } from '@/core/stores/codeStore'
import { useNavigationStore } from '@/core/stores/navigationStore'
import type { TypeUsage } from '@/core/types/library.type'
import { getPathForTypeName } from '@/core/utils/getPathForTypeName'
import { renderTypeUsage } from '@/core/utils/mappers/formatTypeUsage'
import { translateTypeName } from '@/core/utils/translateTypeName'

import styles from './TypeLink.module.scss'

const valueTypeClassName = methodInfoStyles.valueType

type Props = {
  typeName?: string
  typeUsage?: TypeUsage
  className?: string
}

export function TypeLink({ typeName, typeUsage, className }: Props) {
  const { libraries } = useLibraries()
  const navigateTo = useNavigationStore((s) => s.navigateTo)
  const selectedTechnology = useCodeStore((s) => s.selectedTechnology)

  const rawLabel = typeof typeName === 'string' ? typeName : ''
  const displayLabel = useMemo(() => {
    if (typeUsage) return renderTypeUsage(typeUsage, selectedTechnology)
    return translateTypeName(rawLabel, selectedTechnology)
  }, [typeUsage, rawLabel, selectedTechnology])

  const path = useMemo(() => getPathForTypeName(libraries ?? {}, rawLabel), [libraries, rawLabel])
  const handleClick = useCallback(() => {
    if (path) {
      navigateTo(path)
    }
  }, [path, navigateTo])

  const combinedClassName = className ? `${valueTypeClassName} ${className}` : valueTypeClassName

  if (!displayLabel.trim() || !path) {
    return (
      <span className={styles.typeWithHint}>
        <span className={combinedClassName}>{displayLabel}</span>
        <TypeGraftabilityHint typeUsage={typeUsage} />
      </span>
    )
  }

  return (
    <span className={styles.typeWithHint}>
      <button
        type="button"
        className={`${styles.resetButton} ${combinedClassName}`}
        onClick={handleClick}
        title={`Go to ${rawLabel}`}
      >
        {displayLabel}
      </button>
      <TypeGraftabilityHint typeUsage={typeUsage} />
    </span>
  )
}
